import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefCallback } from "react"
import {
  Bot,
  ChevronDown,
  FileText,
  History,
  List,
  Map,
  Share2,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react"
import {
  animate,
  motion,
  useDragControls,
  useMotionValue,
  useReducedMotion,
  type PanInfo,
} from "motion/react"
import { useMobileViewport } from "../../hooks/useMobileViewport"
import type { ActionCard } from "../../hooks/useComputedDirective"
import type { CapabilityNavigationHandler } from "../../config/capabilityNavigation"
import { ActivityCenterPopover } from "./ActivityCenterPopover"
import { ChatInput } from "./ChatInput"
import {
  computeProjectedDetent,
  useBearingSheetDetents,
  type BearingSheetDetent,
} from "./useBearingSheetDetents"
import type {
  AmbientContextChip,
  AmbientSheetDefault,
} from "../../context/AmbientContextProvider"

// Physics-defined spring (NOT duration/bounce): Motion's resolver zeros injected
// velocity on the duration/bounce path, which would kill the drag-release handoff.
// stiffness/damping/mass ≈ Apple's damping-ratio 0.8, response ~0.3s.
const SHEET_SPRING = { type: "spring" as const, stiffness: 439, damping: 33.5, mass: 1 }
const REDUCED_MOTION_TRANSITION = { duration: 0.2, ease: "easeOut" as const }
// Park fully below the viewport until ResizeObserver reports real heights, so the
// sheet is never a full-screen blocker in the frames before it can be measured.
const OFFSCREEN_Y = 2000

export function isBearingComposerTextEntryTarget(
  target: EventTarget | null,
): target is HTMLTextAreaElement {
  return target instanceof HTMLTextAreaElement
}

const SHEET_ACTIONS = [
  {
    id: "create",
    label: "Create Something",
    fallbackDescription: "Generate a new project artifact with Orca.",
    Icon: Sparkles,
  },
  {
    id: "agent_activation",
    label: "Activate Agent",
    fallbackDescription: "Start an Agent workflow with approval.",
    Icon: Bot,
  },
] as const

const REPORT_ESSENTIALS = [
  {
    id: "report",
    label: "Open Report",
    description: "Answer-first summary, status, date, version, and complete Report.",
    Icon: FileText,
  },
  {
    id: "sections",
    label: "Report Sections",
    description: "Browse the complete nine-category Report.",
    Icon: List,
  },
  {
    id: "evidence",
    label: "Evidence & Verification",
    description: "Sources, assumptions, conflicts, uncertainty, and verification needs.",
    Icon: ShieldCheck,
  },
  {
    id: "roadmap",
    label: "Actions & Roadmap",
    description: "Prioritized, report-scoped next steps and progress.",
    Icon: Map,
  },
  {
    id: "versions",
    label: "Versions & Changes",
    description: "Dated history; Dynamic change review and confirmed refresh when available.",
    Icon: History,
  },
  {
    id: "export",
    label: "Export & Share",
    description: "Download PDF, print, share, and keep each delivered version.",
    Icon: Share2,
  },
] as const

function getSheetActionPresentation(actionCards: ActionCard[]) {
  return SHEET_ACTIONS.map((action) => {
    const liveCard = actionCards.find((card) => card.id === action.id)
    const liveDescription = liveCard?.description.replace(/\s+/g, " ").trim()

    return {
      ...action,
      available: Boolean(liveCard),
      description: liveCard
        ? liveDescription || action.fallbackDescription
        : action.fallbackDescription,
    }
  })
}

function measuredHeight(entry: ResizeObserverEntry): number {
  return entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height
}

interface BearingComposerSheetProps {
  /** Off-shell handoff only; shell routes provide onComposerHostChange instead. */
  onSend?: (message: string) => void
  onAttachFiles?: () => void
  onTakePhoto?: () => void
  isLoading?: boolean
  isStreaming?: boolean
  isConversationActive?: boolean
  /** Focused Show content keeps the chat surface available at its Down detent. */
  showFocused?: boolean
  onPromptSelect?: (prompt: string) => void
  onActionSelect?: (id: string) => void
  activityCenterOpen?: boolean
  onActivityCenterOpenChange?: (open: boolean) => void
  onActivityCenterCapabilityOpen?: CapabilityNavigationHandler
  onActivityCenterAttentionChange?: (hasAttention: boolean) => void
  actionCards?: ActionCard[]
  ambientContextChip?: AmbientContextChip | null
  ambientSheetDefault?: AmbientSheetDefault | null
  ambientFocusSignature?: string | null
  onAmbientContextDismiss?: () => void
  contextualRequestActive?: boolean
  onContextualRequestDismiss?: () => void
  /** DOM host for ChatPane's one live directive presentation. */
  onDirectiveHostChange?: RefCallback<HTMLDivElement>
  /** DOM host for ChatPane's one real ChatInput instance. */
  onComposerHostChange?: RefCallback<HTMLDivElement>
  /** DOM host for the one mobile ChatPane tree. The shell owns the runtime; the
   * sheet owns only its mobile placement. */
  onChatHostChange?: RefCallback<HTMLDivElement>
  onDetentChange?: (detent: BearingSheetDetent) => void
}

export function BearingComposerSheet({
  onSend,
  onAttachFiles,
  onTakePhoto,
  isLoading = false,
  isStreaming = false,
  isConversationActive = false,
  showFocused = false,
  onPromptSelect,
  onActionSelect,
  activityCenterOpen = false,
  onActivityCenterOpenChange,
  onActivityCenterCapabilityOpen,
  onActivityCenterAttentionChange,
  actionCards = [],
  ambientContextChip = null,
  ambientSheetDefault = null,
  ambientFocusSignature = null,
  onAmbientContextDismiss,
  contextualRequestActive = false,
  onContextualRequestDismiss,
  onDirectiveHostChange,
  onComposerHostChange,
  onChatHostChange,
  onDetentChange,
}: BearingComposerSheetProps = {}) {
  const [fallbackValue, setFallbackValue] = useState("")
  const handlePromptSelect = useCallback((prompt: string) => {
    if (onPromptSelect) {
      onPromptSelect(prompt)
    } else {
      setFallbackValue(prompt)
    }
  }, [onPromptSelect])
  const sheetRef = useRef<HTMLDivElement>(null)
  const peekRef = useRef<HTMLDivElement>(null)
  const guidanceRef = useRef<HTMLElement>(null)
  const composerHostRef = useRef<HTMLDivElement>(null)
  const closeControlRef = useRef<HTMLButtonElement>(null)
  const [contextDisclosureVisible, setContextDisclosureVisible] = useState(false)
  const [fullHeightPx, setFullHeightPx] = useState(0)
  const [guidanceHeightPx, setGuidanceHeightPx] = useState(64)
  const [composerHeightPx, setComposerHeightPx] = useState(0)
  const { keyboardHeight } = useMobileViewport()
  const prefersReducedMotion = useReducedMotion()
  const dragControls = useDragControls()
  const handlePointerStartedRef = useRef(false)
  const suppressHandleClickRef = useRef(false)
  const sheetDragStartedRef = useRef(false)

  useLayoutEffect(() => {
    const sheetEl = sheetRef.current
    const peekEl = peekRef.current
    const guidanceEl = guidanceRef.current
    if (!sheetEl || !peekEl || !guidanceEl) return
    // Measure synchronously up front — layout is settled inside useLayoutEffect, so
    // the sheet is positioned on the first paint instead of waiting a frame for the
    // async ResizeObserver callback (which is what left it parked off-screen).
    setFullHeightPx(sheetEl.getBoundingClientRect().height)
    setGuidanceHeightPx(guidanceEl.getBoundingClientRect().height)
    setComposerHeightPx(peekEl.getBoundingClientRect().height)
    const sheetObserver = new ResizeObserver((entries) => setFullHeightPx(measuredHeight(entries[0])))
    const guidanceObserver = new ResizeObserver((entries) => setGuidanceHeightPx(measuredHeight(entries[0])))
    const peekObserver = new ResizeObserver((entries) => setComposerHeightPx(measuredHeight(entries[0])))
    sheetObserver.observe(sheetEl)
    guidanceObserver.observe(guidanceEl)
    peekObserver.observe(peekEl)
    return () => {
      sheetObserver.disconnect()
      guidanceObserver.disconnect()
      peekObserver.disconnect()
    }
  }, [])

  const { detent, setDetent, heights } = useBearingSheetDetents({
    guidanceHeightPx,
    composerHeightPx,
    fullHeightPx,
  })
  const detentRef = useRef(detent)
  detentRef.current = detent

  // y = the sheet's translateY. 0 = Full (fully up). Each other state maps to
  // full minus its visible height. Only transform animates; the
  // sheet's own CSS height is always `full`.
  const y = useMotionValue(OFFSCREEN_Y)

  useEffect(() => {
    const publishVisibleHeight = (translateY: number) => {
      const visibleHeight = detentRef.current === "closed"
        ? 0
        : Math.max(0, fullHeightPx - translateY)
      document.documentElement.style.setProperty(
        "--bearing-composer-visible-height",
        `${visibleHeight}px`,
      )
    }

    publishVisibleHeight(y.get())
    const unsubscribe = y.on("change", publishVisibleHeight)
    return () => {
      unsubscribe()
      document.documentElement.style.removeProperty("--bearing-composer-visible-height")
    }
  }, [fullHeightPx, y])

  const snapTo = useCallback(
    (next: BearingSheetDetent, releaseVelocityYPxPerSec = 0) => {
      const targetY = heights.full - heights[next]
      if (prefersReducedMotion) {
        animate(y, targetY, REDUCED_MOTION_TRANSITION)
      } else {
        animate(y, targetY, { ...SHEET_SPRING, velocity: releaseVelocityYPxPerSec })
      }
      setDetent(next)
      onDetentChange?.(next)
    },
    [heights, onDetentChange, prefersReducedMotion, setDetent, y],
  )

  const closeSheet = useCallback(() => {
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement && sheetRef.current?.contains(activeElement)) {
      activeElement.blur()
    }
    if (contextualRequestActive) onContextualRequestDismiss?.()
    snapTo("closed")
  }, [contextualRequestActive, onContextualRequestDismiss, snapTo])

  const handleComposerHostChange = useCallback((node: HTMLDivElement | null) => {
    composerHostRef.current = node
    onComposerHostChange?.(node)
  }, [onComposerHostChange])

  useEffect(() => {
    const composerHost = composerHostRef.current
    if (!composerHost) return

    const handleComposerFocus = (event: FocusEvent) => {
      setContextDisclosureVisible(true)
      if (!isConversationActive && isBearingComposerTextEntryTarget(event.target)) {
        snapTo("half")
      }
    }

    composerHost.addEventListener("focusin", handleComposerFocus)
    return () => composerHost.removeEventListener("focusin", handleComposerFocus)
  }, [isConversationActive, snapTo])

  useEffect(() => {
    setContextDisclosureVisible(false)
  }, [ambientContextChip?.contextVersion])

  useEffect(() => {
    const restoreChat = (event: Event) => {
      const requested = (event as CustomEvent<{ detent?: BearingSheetDetent }>).detail?.detent
      const next: BearingSheetDetent = requested === "closed"
        || requested === "guidance"
        || requested === "composer"
        || requested === "half"
        || requested === "full"
        ? requested
        : "guidance"
      snapTo(next)
      if (next === "guidance") {
        window.requestAnimationFrame(() => closeControlRef.current?.focus())
      }
    }
    window.addEventListener("orca:restoreComposerSheet", restoreChat)
    return () => window.removeEventListener("orca:restoreComposerSheet", restoreChat)
  }, [snapTo])

  useEffect(() => {
    if (contextualRequestActive) snapTo("full")
  }, [contextualRequestActive, snapTo])

  useEffect(() => {
    if (isConversationActive) snapTo("composer")
  }, [isConversationActive, snapTo])

  const previousShowFocusedRef = useRef(false)
  useEffect(() => {
    if (previousShowFocusedRef.current === showFocused) return
    previousShowFocusedRef.current = showFocused
    if (!showFocused) {
      snapTo("composer")
    } else if (!ambientFocusSignature) {
      snapTo("guidance")
    }
  }, [ambientFocusSignature, showFocused, snapTo])

  const appliedFocusSignatureRef = useRef<string | null>(null)
  useEffect(() => {
    if (!showFocused || isConversationActive || !ambientFocusSignature) return
    if (appliedFocusSignatureRef.current === ambientFocusSignature) return
    appliedFocusSignatureRef.current = ambientFocusSignature
    snapTo(ambientSheetDefault === "closed" ? "closed" : "guidance")
  }, [ambientFocusSignature, ambientSheetDefault, isConversationActive, showFocused, snapTo])

  // Re-clamp to the current detent on a real measurement change (resize, keyboard,
  // orientation). detentRef keeps this independent of `detent` changing on its own,
  // so a drag-release's velocity-seeded snap isn't clobbered. hasSettledRef makes
  // the first real measurement a jump (no entrance spring on mount).
  const hasSettledRef = useRef(false)
  useLayoutEffect(() => {
    // Until real measurements arrive, park OFF-SCREEN (never y=0/full-screen, which
    // would cover the app). Always drive y so a late measurement still lands the
    // sheet at its detent — it can never get stuck off-screen.
    const hasRealMeasurement = fullHeightPx > 0 && composerHeightPx > 0
    const targetY = hasRealMeasurement
      ? heights.full - heights[detentRef.current]
      : OFFSCREEN_Y
    if (!hasSettledRef.current) {
      y.jump(targetY)
      if (hasRealMeasurement) hasSettledRef.current = true
    } else if (prefersReducedMotion) {
      animate(y, targetY, REDUCED_MOTION_TRANSITION)
    } else {
      animate(y, targetY, SHEET_SPRING)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heights, prefersReducedMotion, y])

  const handleDragEnd = useCallback(
    (_event: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
      const currentHeightPx = heights.full - y.get()
      // velocity.y is positive moving DOWN the screen (sheet shrinking) — negate
      // for computeProjectedDetent's "growing = positive" convention. The raw
      // info.velocity.y seeds the y-animation directly (y tracks the pointer 1:1).
      const next = computeProjectedDetent(heights, currentHeightPx, -info.velocity.y)
      snapTo(next, info.velocity.y)
    },
    [heights, snapTo, y],
  )

  const sheetClosed = detent === "closed"
  const revealInert = detent === "closed"
    || detent === "composer"
    || detent === "guidance"

  // Portal into the composer's full-viewport layer so popovers escape the
  // sheet's overflow:hidden while remaining in the same stacking context. The
  // shell root fallback keeps standalone/component renders usable.
  const portalTarget =
    (sheetRef.current?.closest(".bearing-global-composer-layer") as HTMLElement | null) ??
    (sheetRef.current?.closest(".orca-engine-4pane") as HTMLElement | null) ??
    null
  const sheetActions = getSheetActionPresentation(actionCards)
  const activityCenterController = (
    <ActivityCenterPopover
      open={activityCenterOpen}
      onOpenChange={(open) => onActivityCenterOpenChange?.(open)}
      onOpenCapability={(target) => onActivityCenterCapabilityOpen?.(target)}
      onAttentionChange={onActivityCenterAttentionChange}
      triggerVariant="quick-access"
      portalTarget={portalTarget}
      hideTrigger
    />
  )

  return (
    <motion.div
      ref={sheetRef}
      className="bearing-home-composer"
      data-conversation-active={isConversationActive ? "true" : "false"}
      data-request-active={isLoading || isStreaming ? "true" : "false"}
      data-contextual-request-active={contextualRequestActive ? "true" : "false"}
      data-detent={detent}
      aria-hidden={sheetClosed ? "true" : undefined}
      inert={sheetClosed ? ("" as unknown as boolean) : undefined}
      style={{
        y,
        height: `calc(100dvh - env(safe-area-inset-top, 0px) - ${keyboardHeight}px)`,
      }}
      drag={sheetClosed ? false : "y"}
      dragListener={false}
      dragControls={dragControls}
      dragMomentum={false}
      dragConstraints={{ top: 0, bottom: heights.full - heights.guidance }}
      dragElastic={0.12}
      onDragStart={() => {
        sheetDragStartedRef.current = true
        if (handlePointerStartedRef.current) suppressHandleClickRef.current = true
      }}
      onDragEnd={handleDragEnd}
      onPointerDown={(event) => {
        if (sheetClosed) return
        const target = event.target as HTMLElement
        sheetDragStartedRef.current = false
        if (target.closest(".bearing-home-composer-drag-handle")) {
          handlePointerStartedRef.current = true
          suppressHandleClickRef.current = false
          dragControls.start(event)
          return
        }
        handlePointerStartedRef.current = false
        // Keep the reveal rail vertically scrollable on shorter phones. The drag
        // header and remaining sheet surface still provide generous grab areas.
        if (target.closest("input, button, a, .bearing-home-composer-reveal")) return
        dragControls.start(event)
      }}
    >
      <div
        ref={peekRef}
        className="bearing-home-composer-peek"
        role="group"
        aria-label="Connected Directive Composer"
        data-connected-directive-composer="sheet"
      >
        <section
          ref={guidanceRef}
          className="bearing-home-composer-drag"
          aria-label="Current project guidance"
          onClick={(event) => {
            if (detent !== "guidance") return
            if (sheetDragStartedRef.current) {
              sheetDragStartedRef.current = false
              return
            }
            if ((event.target as HTMLElement).closest(".bearing-home-composer-drag-handle")) return
            snapTo("composer")
          }}
        >
          <div
            ref={onDirectiveHostChange}
            className="bearing-sheet-directive-host"
          />
          <button
            ref={closeControlRef}
            type="button"
            className="bearing-home-composer-drag-handle"
            aria-label={detent === "guidance" ? "Open Chat composer" : "Close Chat"}
            onClick={(event) => {
              if (suppressHandleClickRef.current) {
                event.preventDefault()
                suppressHandleClickRef.current = false
                sheetDragStartedRef.current = false
                return
              }
              if (detent === "guidance") {
                snapTo("composer")
              } else {
                closeSheet()
              }
            }}
          >
            <span aria-hidden="true" />
          </button>
        </section>
        <div
          ref={handleComposerHostChange}
          className="bearing-sheet-composer-host"
          aria-label="Persistent Orca chat input"
        >
          {!onComposerHostChange && (
            <ChatInput
              variant="discovery"
              sheetComposer
              compact
              value={fallbackValue}
              onChange={setFallbackValue}
              onSubmit={() => {
                const message = fallbackValue.trim()
                if (!message) return
                onSend?.(message)
                setFallbackValue("")
                snapTo("half")
              }}
              isLoading={false}
              onPromptSelect={handlePromptSelect}
              onQuickAction={(action) => {
                if (action === "attach") onAttachFiles?.()
                if (action === "camera") onTakePhoto?.()
              }}
            />
          )}
        </div>
      </div>

      {/* Reveal — expanded chat tools. Hidden at the Composer peek; slides in
          when the sheet is dragged up. */}
      <div className="bearing-home-composer-reveal" aria-hidden={revealInert}>
        <div
          ref={onChatHostChange}
          className="bearing-sheet-chat-host"
          aria-hidden={revealInert}
          inert={revealInert ? ("" as unknown as boolean) : undefined}
        />
        {!contextualRequestActive && ambientContextChip && contextDisclosureVisible && (
          <details
            className="bearing-ambient-context-disclosure"
            data-context-version={ambientContextChip.contextVersion}
          >
            <summary className="bearing-ambient-context-summary">
              <span className="bearing-ambient-context-summary-label">
                Context: {ambientContextChip.label}
              </span>
              <ChevronDown
                className="bearing-ambient-context-chevron"
                size={18}
                strokeWidth={1.9}
                aria-hidden="true"
              />
            </summary>
            <div className="bearing-ambient-context-details">
              <strong>{ambientContextChip.label}</strong>
              <p>Orca will use this screen to answer your next message.</p>
              <button type="button" onClick={onAmbientContextDismiss}>
                Remove context
              </button>
            </div>
          </details>
        )}
        {!contextualRequestActive && <section className="bearing-home-action-section" aria-labelledby="bearing-home-actions-label">
          <span id="bearing-home-actions-label" className="bearing-home-quick-label">Actions</span>
          <div className="bearing-home-action-grid">
            {sheetActions.map((action) => {
              const Icon = action.Icon
              return (
                <button
                  type="button"
                  key={action.id}
                  className="bearing-home-action-card"
                  data-action-id={action.id}
                  tabIndex={revealInert ? -1 : 0}
                  disabled={!action.available || !onActionSelect}
                  onClick={() => onActionSelect?.(action.id)}
                >
                  <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
                  <span className="bearing-home-action-copy">
                    <span className="bearing-home-action-title">{action.label}</span>
                    <span className="bearing-home-action-description">{action.description}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </section>}

        {!contextualRequestActive && <section className="bearing-home-action-section bearing-home-report-section" aria-labelledby="bearing-home-report-label">
          <span id="bearing-home-report-label" className="bearing-home-quick-label">Report essentials</span>
          <div className="bearing-home-action-grid bearing-home-report-grid">
            {REPORT_ESSENTIALS.map((item) => {
              const Icon = item.Icon
              return (
                <button
                  type="button"
                  key={item.id}
                  className="bearing-home-action-card"
                  data-report-destination={item.id}
                  disabled
                >
                  <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
                  <span className="bearing-home-action-copy">
                    <span className="bearing-home-action-title">{item.label}</span>
                    <span className="bearing-home-action-description">{item.description}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </section>}
      </div>

      {activityCenterController}

    </motion.div>
  )
}

export default BearingComposerSheet
